import type { ChangeEvent } from 'react';
import {
  ACCESSIBILITY_OPTIONS,
  ACTIVITY_STATUS_OPTIONS,
  AGRICULTURAL_INTEGRATION_OPTIONS,
  AGRITOUR_SECTIONS,
  BUILDING_ELEMENTS_OPTIONS,
  READINESS_OPTIONS,
  REQUIRED_CONNECTIONS_OPTIONS,
  SANITATION_OPTIONS,
  SEASONAL_OR_PERMANENT_OPTIONS,
  SHADED_GATHERING_OPTIONS,
  SIMULTANEOUS_CAPACITY_OPTIONS,
  VENTURE_LOCATION_OPTIONS,
  YES_NO,
} from './agritour-form-copy';
import type { AgriTourFormData, AgriTourFiles } from './AgriTourScreen';
import AgriTourSelect from './AgriTourSelect';
import styles from './AgriTourScreen.module.css';
interface AgriTourStepCProps {
  formData: AgriTourFormData;
  files: AgriTourFiles;
  fieldErrors: Record<string, string>;
  onChange: (patch: Partial<AgriTourFormData>) => void;
  onProjectPlansAdd: (files: File[]) => void;
  onProjectPlansRemove: (index: number) => void;
}

function toggleArrayValue(arr: number[], val: number): number[] {
  return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
}

export default function AgriTourStepC({
  formData,
  files,
  fieldErrors,
  onChange,
  onProjectPlansAdd,
  onProjectPlansRemove,
}: AgriTourStepCProps) {
  const handleProjectPlansPick = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files;
    if (!picked?.length) return;
    onProjectPlansAdd(Array.from(picked));
    e.target.value = '';
  };

  return (
    <section aria-label={AGRITOUR_SECTIONS.c.title}>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>תוכניות המיזם הקיימות</label>
        <p className={styles.fieldHint}>ניתן לבחור מספר קבצים</p>
        <div className={styles.fileRow}>
          <label className={styles.fileButton}>
            {files.file_mm4mw3k7.length > 0 ? 'הוסף קבצים' : 'בחר קבצים'}
            <input
              type="file"
              multiple
              style={{ display: 'none' }}
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleProjectPlansPick}
            />
          </label>
        </div>
        {files.file_mm4mw3k7.length > 0 ? (
          <ul className={styles.fileList} aria-label="תוכניות המיזם שנבחרו">
            {files.file_mm4mw3k7.map((file, index) => (
              <li key={`${file.name}-${index}`} className={styles.fileListItem}>
                <span className={styles.fileName}>{file.name}</span>
                <button
                  type="button"
                  className={styles.fileRemove}
                  onClick={() => onProjectPlansRemove(index)}
                >
                  הסר
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>מיקום המיזם</label>
        <p className={styles.fieldHint}>אנא בחר את מיקום המיזם מתוך האפשרויות המוצגות.</p>
        <AgriTourSelect
          value={formData.ventureLocation}
          options={VENTURE_LOCATION_OPTIONS}
          onChange={(v) => onChange({ ventureLocation: v })}
          hasError={Boolean(fieldErrors.ventureLocation)}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          סטטוס פעילות המיזם
        </label>
        <AgriTourSelect
          value={formData.ventureActivityStatus}
          options={ACTIVITY_STATUS_OPTIONS}
          onChange={(v) => onChange({ ventureActivityStatus: v })}
          hasError={Boolean(fieldErrors.ventureActivityStatus)}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          רמת בשלות ומוכנות של הפרויקט
        </label>
        <AgriTourSelect
          value={formData.readinessLevel}
          options={READINESS_OPTIONS}
          onChange={(v) => onChange({ readinessLevel: v })}
          hasError={Boolean(fieldErrors.readinessLevel)}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          זיקה ואינטגרציה עם פעילות חקלאית פעילה
        </label>
        <p className={styles.fieldHint}>
          האם המיזם מתוכנן להשתלב, או משתלב כבר כיום, בפעילות חקלאית יצרנית ופעילה של המשק?
        </p>
        <AgriTourSelect
          value={formData.agriculturalIntegration}
          options={AGRICULTURAL_INTEGRATION_OPTIONS}
          onChange={(v) => onChange({ agriculturalIntegration: v })}
          hasError={Boolean(fieldErrors.agriculturalIntegration)}
        />
      </div>
      {formData.agriculturalIntegration === '0' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>
            שילוב המיזם בפעילות חקלאית יצרנית ופעילה של המשק
          </label>
          <p className={styles.fieldHint}>
            <em>אנא פרט מהו הענף החקלאי הפעיל במשק</em>
          </p>
          <input
            type="text"
            className={styles.input}
            value={formData.agriculturalDetail}
            onChange={(e) => onChange({ agriculturalDetail: e.target.value })}
          />
        </div>
      )}

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          מאפייני הבינוי והאלמנטים הפיזיים המבוקשים בבקשה
        </label>
        <p className={styles.fieldHint}>יש לסמן את כל המאפיינים והאלמנטים הרלוונטים</p>
        <div
          className={`${styles.checkboxGroup} ${fieldErrors.buildingElements ? styles.checkboxGroupError : ''}`}
        >
          {BUILDING_ELEMENTS_OPTIONS.map((o) => (
            <label key={o.value} className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.buildingElements.includes(o.value)}
                onChange={() =>
                  onChange({ buildingElements: toggleArrayValue(formData.buildingElements, o.value) })
                }
              />
              {o.label}
            </label>
          ))}
        </div>
        {fieldErrors.buildingElements && (
          <p className={styles.fieldError} role="alert">{fieldErrors.buildingElements}</p>
        )}
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          שטח המיזם המשוער שמיועד לטובת חוויית המבקרים או המבנה (במ״ר)
        </label>
        <p className={styles.fieldHint}>
          שימו לב: מרכז מבקרים מומלץ דורש מרחב פעילות של 80 מ&quot;ר בנוי ומעלה לאירוח קבוצות
        </p>
        <input
          type="number"
          className={`${styles.input} ${fieldErrors.ventureAreaSqm ? styles.inputError : ''}`}
          value={formData.ventureAreaSqm}
          onChange={(e) => onChange({ ventureAreaSqm: e.target.value })}
          min={0}
          style={{ maxWidth: 200 }}
        />
        {fieldErrors.ventureAreaSqm && (
          <p className={styles.fieldError} role="alert">{fieldErrors.ventureAreaSqm}</p>
        )}
      </div>
      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>מספר עובדים</label>
        <p className={styles.fieldHint}>כמה עובדים קבועים ועונתיים יועסקו במיזם?</p>
        <input
          type="number"
          className={`${styles.input} ${fieldErrors.numEmployees ? styles.inputError : ''}`}
          value={formData.numEmployees}
          onChange={(e) => onChange({ numEmployees: e.target.value })}
          min={0}
          style={{ maxWidth: 160 }}
        />
        {fieldErrors.numEmployees && (
          <p className={styles.fieldError} role="alert">{fieldErrors.numEmployees}</p>
        )}
      </div>

      <h3 className={styles.sectionSubhead}>{AGRITOUR_SECTIONS.c.utilities}</h3>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          האם קיימות כיום תשתיות פיזיות זמינות בנקודת המיזם?
        </label>
        <AgriTourSelect
          value={formData.hasPhysicalInfrastructure}
          options={YES_NO}
          onChange={(v) => onChange({ hasPhysicalInfrastructure: v })}
          hasError={Boolean(fieldErrors.hasPhysicalInfrastructure)}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          האם המיזם דורש חיבורים חדשים או הגדלת חיבורים קיימים למים, חשמל או ביוב?
        </label>
        <p className={styles.fieldHint}>סמן את כל החיבורים הנדרשים.</p>
        <div
          className={`${styles.checkboxGroup} ${fieldErrors.requiredConnections ? styles.checkboxGroupError : ''}`}
        >
          {REQUIRED_CONNECTIONS_OPTIONS.map((o) => (
            <label key={o.value} className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.requiredConnections.includes(o.value)}
                onChange={() =>
                  onChange({
                    requiredConnections: toggleArrayValue(formData.requiredConnections, o.value),
                  })
                }
              />
              {o.label}
            </label>
          ))}
        </div>
        {fieldErrors.requiredConnections && (
          <p className={styles.fieldError} role="alert">{fieldErrors.requiredConnections}</p>
        )}
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          אזור התכנסות מוצל ושירותי תברואה למבקרים
        </label>
        <p className={styles.fieldHint}>
          <strong>
            האם קיים או מתוכנן בפרויקט אזור התכנסות חיצוני מוצל / מקורה (פרגולה, סככה, אוהל)
            עבור קבוצות ומבקרים?
          </strong>
        </p>
        <AgriTourSelect
          value={formData.shadedGatheringArea}
          options={SHADED_GATHERING_OPTIONS}
          onChange={(v) => onChange({ shadedGatheringArea: v })}
          hasError={Boolean(fieldErrors.shadedGatheringArea)}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          פתרון שירותי הנוחיות/תברואה הקיים או המתוכנן עבור קהל המבקרים במקום
        </label>
        <p className={styles.fieldHint}>
          אנא בחר את הפתרון הקיים או המתוכנן לשירותי תברואה עבור קהל המבקרים במקום.
        </p>
        <AgriTourSelect
          value={formData.sanitationSolution}
          options={SANITATION_OPTIONS}
          onChange={(v) => onChange({ sanitationSolution: v })}
          hasError={Boolean(fieldErrors.sanitationSolution)}
        />
      </div>

      <h3 className={styles.sectionSubhead}>{AGRITOUR_SECTIONS.c.accessParking}</h3>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          האם קיימת כיום דרך גישה סטטוטורית סלולה אל המיזם?
        </label>
        <AgriTourSelect
          value={formData.hasStatutoryAccessRoad}
          options={YES_NO}
          onChange={(v) => onChange({ hasStatutoryAccessRoad: v })}
          hasError={Boolean(fieldErrors.hasStatutoryAccessRoad)}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>נגישות המקום להגעה</label>
        <p className={styles.fieldHint}>אנא בחר את סוג הנגישות המתאים למקום.</p>
        <AgriTourSelect
          value={formData.accessibilityType}
          options={ACCESSIBILITY_OPTIONS}
          onChange={(v) => onChange({ accessibilityType: v })}
          hasError={Boolean(fieldErrors.accessibilityType)}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          האם קיימת חנייה מוסדרת במקום?
        </label>
        <AgriTourSelect
          value={formData.hasOrganizedParking}
          options={YES_NO}
          onChange={(v) => onChange({ hasOrganizedParking: v })}
          hasError={Boolean(fieldErrors.hasOrganizedParking)}
        />
      </div>
      {formData.hasOrganizedParking === '0' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>
            חניה מסודרת במקום - מספר מקומות
          </label>
          <p className={styles.fieldHint}>ציין את מספר המקומות של חניה מסודרת במקום</p>
          <input
            type="number"
            className={styles.input}
            value={formData.existingParkingSpots}
            onChange={(e) => onChange({ existingParkingSpots: e.target.value })}
            min={0}
            style={{ maxWidth: 160 }}
          />
        </div>
      )}

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          האם יש צורך בהסדרת חנייה חדשה או הרחבתה כחלק מהפרויקט?
        </label>
        <AgriTourSelect
          value={formData.needsNewParking}
          options={YES_NO}
          onChange={(v) => onChange({ needsNewParking: v })}
          hasError={Boolean(fieldErrors.needsNewParking)}
        />
      </div>
      {formData.needsNewParking === '0' && (
        <div className={`${styles.field} ${styles.conditional}`} style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <label className={styles.fieldLabel}>
              הערכת מספר מקומות חנייה נדרשים לפעילות מלאה - רכבים פרטיים
            </label>
            <input
              type="number"
              className={styles.input}
              value={formData.parkingCarsNeeded}
              onChange={(e) => onChange({ parkingCarsNeeded: e.target.value })}
              min={0}
            />
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <label className={styles.fieldLabel}>
              הערכת מספר מקומות חנייה נדרשים לפעילות מלאה - אוטובוסים
            </label>
            <input
              type="number"
              className={styles.input}
              value={formData.parkingBusesNeeded}
              onChange={(e) => onChange({ parkingBusesNeeded: e.target.value })}
              min={0}
            />
          </div>
        </div>
      )}

      <h3 className={styles.sectionSubhead}>{AGRITOUR_SECTIONS.c.hoursLighting}</h3>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          האם מדובר במיזם עונתי או קבוע?
        </label>
        <AgriTourSelect
          value={formData.seasonalOrPermanent}
          options={SEASONAL_OR_PERMANENT_OPTIONS}
          onChange={(v) => onChange({ seasonalOrPermanent: v })}
          hasError={Boolean(fieldErrors.seasonalOrPermanent)}
        />
      </div>
      {formData.seasonalOrPermanent === '0' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>באילו עונות תתקיים הפעילות?</label>
          <input
            type="text"
            className={styles.input}
            value={formData.activitySeasons}
            onChange={(e) => onChange({ activitySeasons: e.target.value })}
          />
        </div>
      )}

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          שעות הפעילות בימי חול ובסופי שבוע
        </label>
        <p className={styles.fieldHint}>נא פרטו את שעות הפעילות הצפויות במיזם בימי חול ובסופי שבוע</p>
        <input
          type="text"
          className={styles.input}
          value={formData.activityHours}
          onChange={(e) => onChange({ activityHours: e.target.value })}
          placeholder="לדוגמה: 08:00-18:00"

        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          האם תתקיים פעילות בשעות החשיכה / לילה?
        </label>
        <AgriTourSelect
          value={formData.nightActivity}
          options={YES_NO}
          onChange={(v) => onChange({ nightActivity: v })}
          hasError={Boolean(fieldErrors.nightActivity)}
        />
      </div>
      {formData.nightActivity === '0' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>
            האם הפעילות בשעות החשיכה תכלול תאורת חוץ קבועה?
          </label>
          <AgriTourSelect
          value={formData.outdoorLighting}
          options={YES_NO}
          onChange={(v) => onChange({ outdoorLighting: v })}
          hasError={Boolean(fieldErrors.outdoorLighting)}
        />
        </div>
      )}

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          האם המיזם יעשה שימוש במערכות הגברה / מוזיקה בשטח פתוח?
        </label>
        <AgriTourSelect
          value={formData.amplificationSystem}
          options={YES_NO}
          onChange={(v) => onChange({ amplificationSystem: v })}
          hasError={Boolean(fieldErrors.amplificationSystem)}
        />
      </div>

      <h3 className={styles.sectionSubhead}>{AGRITOUR_SECTIONS.c.visitors}</h3>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          מהו צפי המבקרים הממוצע ביום פעילות רגיל?
        </label>
        <input
          type="number"
          className={styles.input}
          value={formData.avgVisitorsPerDay}
          onChange={(e) => onChange({ avgVisitorsPerDay: e.target.value })}
          min={0}

          style={{ maxWidth: 200 }}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          היכולת הסימולטנית המתוכננת לקליטת קבוצות בו-זמנית
        </label>
        <p className={styles.fieldHint}>אנא בחר את היכולת הסימולטנית למבקרים בהתאם לתכנון.</p>
        <AgriTourSelect
          value={formData.simultaneousCapacity}
          options={SIMULTANEOUS_CAPACITY_OPTIONS}
          onChange={(v) => onChange({ simultaneousCapacity: v })}
          hasError={Boolean(fieldErrors.simultaneousCapacity)}
        />
      </div>
      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>תרומה לתיירות נכנסת</label>
        <p className={styles.fieldHint}>
          האם המיזם פונה או עתיד לפנות גם לקהל של תיירים מחו&quot;ל (כולל חומרים שיווקיים והדרכות בשפות זרות)?
        </p>
        <AgriTourSelect
          value={formData.inboundTourism}
          options={YES_NO}
          onChange={(v) => onChange({ inboundTourism: v })}
          hasError={Boolean(fieldErrors.inboundTourism)}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          האם המיזם פונה לקהלים נוספים בארץ (בתי ספר, קבוצות מאורגנות)?
        </label>
        <AgriTourSelect
          value={formData.additionalAudiences}
          options={YES_NO}
          onChange={(v) => onChange({ additionalAudiences: v })}
          hasError={Boolean(fieldErrors.additionalAudiences)}
        />
      </div>
    </section>
  );
}
